import React, { useState, useEffect, useCallback } from 'react';
import './Dashboard.css';
import SiteCard from './SiteCard';
import AddSiteModal from './AddSiteModal';
import EditSiteModal from './EditSiteModal';
import UserManagement from './UserManagement';
import logo from '../assets/Zenio+Logo+header.png';
import { api, useLocalStorageFallback } from '../services/api';

// Default sites for localStorage fallback
const DEFAULT_SITES = [
  { id: 1, name: 'Gmail', url: 'https://mail.google.com', icon: 'placeholder' },
  { id: 2, name: 'Calendar', url: 'https://calendar.google.com', icon: 'placeholder' },
  { id: 3, name: 'Drive', url: 'https://drive.google.com', icon: 'placeholder' },
  { id: 4, name: 'Slack', url: 'https://slack.com', icon: 'placeholder' },
  { id: 5, name: 'GitHub', url: 'https://github.com', icon: 'placeholder' },
  { id: 6, name: 'Jira', url: 'https://atlassian.com', icon: 'placeholder' }
];

function Dashboard({ user, onLogout }) {
  const [sites, setSites] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);

  const useFallback = useLocalStorageFallback();

  const loadSites = useCallback(async () => {
    setIsLoading(true);
    setError('');

    if (useFallback) {
      // Use localStorage
      const savedSites = localStorage.getItem('zenio_sites');
      if (savedSites) {
        setSites(JSON.parse(savedSites));
      } else {
        setSites(DEFAULT_SITES);
        localStorage.setItem('zenio_sites', JSON.stringify(DEFAULT_SITES));
      }
      setIsLoading(false);
    } else {
      // Use API
      try {
        const result = await api.getSites();
        if (result.success && result.sites) {
          setSites(result.sites);
        } else {
          setSites([]);
        }
      } catch (error) {
        console.error('Failed to load sites:', error);
        setError('Failed to load sites');
        setSites([]);
      } finally {
        setIsLoading(false);
      }
    }
  }, [useFallback]);

  // Load sites from API or localStorage
  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const handleAddSite = async (newSite) => {
    if (useFallback) {
      // LocalStorage version
      const siteWithId = {
        ...newSite,
        id: Date.now()
      };
      const updatedSites = [...sites, siteWithId];
      setSites(updatedSites);
      localStorage.setItem('zenio_sites', JSON.stringify(updatedSites));
      setShowAddModal(false);
    } else {
      // API version
      try {
        const result = await api.createSite(newSite);
        if (result.success) {
          await loadSites(); // Reload sites from API
          setShowAddModal(false);
        } else {
          setError('Failed to add site');
        }
      } catch (error) {
        console.error('Failed to add site:', error);
        setError('Failed to add site');
      }
    }
  };

  const handleEditSite = (site) => {
    setEditingSite(site);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updatedSite) => {
    if (useFallback) {
      // LocalStorage version
      const updatedSites = sites.map(site => 
        (site.id || site.siteId) === (updatedSite.id || updatedSite.siteId) ? updatedSite : site
      );
      setSites(updatedSites);
      localStorage.setItem('zenio_sites', JSON.stringify(updatedSites));
      setShowEditModal(false);
      setEditingSite(null);
    } else {
      // API version - try to update, fallback to local on failure
      try {
        const result = await api.updateSite(updatedSite.siteId || updatedSite.id, updatedSite);
        if (result.success) {
          await loadSites();
          setShowEditModal(false);
          setEditingSite(null);
        } else {
          // Fallback to localStorage update
          console.log('API update failed, updating locally');
          const updatedSites = sites.map(site => 
            (site.id || site.siteId) === (updatedSite.id || updatedSite.siteId) ? updatedSite : site
          );
          setSites(updatedSites);
          setShowEditModal(false);
          setEditingSite(null);
        }
      } catch (error) {
        console.error('Failed to update site via API, updating locally:', error);
        // Fallback to localStorage update
        const updatedSites = sites.map(site => 
          (site.id || site.siteId) === (updatedSite.id || updatedSite.siteId) ? updatedSite : site
        );
        setSites(updatedSites);
        setShowEditModal(false);
        setEditingSite(null);
      }
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Are you sure you want to remove this site?')) {
      return;
    }

    if (useFallback) {
      // LocalStorage version
      const updatedSites = sites.filter(site => (site.siteId || site.id) !== siteId);
      setSites(updatedSites);
      localStorage.setItem('zenio_sites', JSON.stringify(updatedSites));
    } else {
      // API version
      try {
        const result = await api.deleteSite(siteId);
        if (result.success) {
          await loadSites(); // Reload sites from API
        } else {
          setError('Failed to delete site');
        }
      } catch (error) {
        console.error('Failed to delete site:', error);
        setError('Failed to delete site');
      }
    }
  };

  const handleDragStart = (site) => {
    setDraggedItem(site);
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Allow drop
  };

  const handleDrop = (targetSite) => {
    if (!draggedItem || draggedItem === targetSite) return;

    const draggedIndex = sites.findIndex(s => (s.id || s.siteId) === (draggedItem.id || draggedItem.siteId));
    const targetIndex = sites.findIndex(s => (s.id || s.siteId) === (targetSite.id || targetSite.siteId));

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newSites = [...sites];
    const [removed] = newSites.splice(draggedIndex, 1);
    newSites.splice(targetIndex, 0, removed);

    setSites(newSites);
    
    // Save order
    if (useFallback) {
      localStorage.setItem('zenio_sites', JSON.stringify(newSites));
    }
    // For API, you might want to add an updateOrder endpoint
    
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const filteredSites = sites.filter(site =>
    site.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <div className="header-logo-backdrop">
            <img src={logo} alt="ZenIO Logo" className="header-logo-img" />
          </div>
          <h1>Company Panel</h1>
        </div>
        <div className="header-right">
          <span className="user-name">
            {user.name} {user.isAdmin && <span className="admin-badge">Admin</span>}
          </span>
          {user.isAdmin && (
            <button onClick={() => setShowUserManagement(true)} className="manage-users-button">
              Manage Users
            </button>
          )}
          <button onClick={onLogout} className="logout-button">Logout</button>
        </div>
      </header>

      <div className="dashboard-content">
        {useFallback && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            padding: '10px',
            marginBottom: '15px',
            fontSize: '13px',
            color: '#856404'
          }}>
            <strong>⚠️ Using LocalStorage Mode</strong> - API not configured
          </div>
        )}

        {error && (
          <div style={{
            background: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            padding: '10px',
            marginBottom: '15px',
            fontSize: '13px',
            color: '#721c24'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="controls-bar">
          <input
            type="text"
            className="search-input"
            placeholder="Search sites..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isLoading}
          />
          {user.isAdmin && (
            <button 
              className="add-site-button"
              onClick={() => setShowAddModal(true)}
              disabled={isLoading}
            >
              + Add Site
            </button>
          )}
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
            Loading sites...
          </div>
        ) : (
          <>
            <div className="sites-grid">
              {filteredSites.map(site => (
                <SiteCard
                  key={site.id || site.siteId}
                  site={site}
                  isAdmin={user.isAdmin}
                  onEdit={handleEditSite}
                  onDelete={handleDeleteSite}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  isDragging={draggedItem && (draggedItem.id || draggedItem.siteId) === (site.id || site.siteId)}
                />
              ))}
            </div>

            {filteredSites.length === 0 && (
              <div className="no-sites">
                <p>No sites found</p>
                {user.isAdmin && (
                  <button 
                    className="add-site-button"
                    onClick={() => setShowAddModal(true)}
                  >
                    Add Your First Site
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {showAddModal && (
        <AddSiteModal
          onAdd={handleAddSite}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showEditModal && editingSite && (
        <EditSiteModal
          site={editingSite}
          onSave={handleSaveEdit}
          onClose={() => {
            setShowEditModal(false);
            setEditingSite(null);
          }}
        />
      )}

      {showUserManagement && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
    </div>
  );
}

export default Dashboard;
